import { useEffect, useRef } from "react";

/**
 * Hook to ensure mouse wheel scrolling works on pages where wheel events
 * might not naturally bubble to the document/body.
 * 
 * This is useful for pages with complex layouts where wheel events get
 * captured by container elements and don't reach the document for scrolling.
 * 
 * @example
 * Simple usage:
 * const { ref, onWheel } = usePageScroll();
 * return <Box ref={ref} onWheel={onWheel}>...</Box>
 * 
 * With existing ref:
 * const myRef = useRef<HTMLElement>(null);
 * const { onWheel } = usePageScroll(myRef);
 * return <Box ref={myRef} onWheel={onWheel}>...</Box>
 */
export function usePageScroll(existingRef?: React.RefObject<HTMLElement>) {
  const defaultRef = useRef<HTMLElement>(null);
  const containerRef = existingRef || defaultRef;

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      // Check if target element is scrollable
      const target = e.target as HTMLElement;
      const isTargetScrollable = target && (
        target.scrollHeight > target.clientHeight ||
        target.scrollWidth > target.clientWidth
      );
      
      // Check if page can scroll
      const canPageScroll = document.body.scrollHeight > document.body.clientHeight ||
                           document.documentElement.scrollHeight > document.documentElement.clientHeight;
      
      // If target is not scrollable but page can scroll, manually scroll the page
      // Use smaller scroll step with instant behavior for fine-grained scrolling
      if (!isTargetScrollable && canPageScroll && !e.defaultPrevented) {
        window.scrollBy({
          top: e.deltaY * 0.3,
          left: e.deltaX * 0.3,
          behavior: 'auto'
        });
      }
    };

    // Add wheel listener to document to catch all wheel events
    document.addEventListener("wheel", handleWheel, { passive: true });
    
    return () => {
      document.removeEventListener("wheel", handleWheel);
    };
  }, []);

  // Return handler for onWheel prop
  const handleWheel = (e: React.WheelEvent<HTMLElement>) => {
    // Check if page can scroll
    const canScroll = document.body.scrollHeight > document.body.clientHeight ||
                     document.documentElement.scrollHeight > document.documentElement.clientHeight;
    
    // If page can scroll, manually scroll it
    // Use smaller scroll step with instant behavior for fine-grained scrolling
    if (canScroll && !e.defaultPrevented) {
      window.scrollBy({
        top: e.deltaY * 0.3,
        left: e.deltaX * 0.3,
        behavior: 'auto'
      });
    }
  };

  return {
    ref: containerRef,
    onWheel: handleWheel,
  };
}
